/**
 * The application frame.
 *
 * Desktop is three zones — sidebar, content, and a contextual rail that only
 * exists when there is context to show. Mobile is a different layout, not a
 * squeezed one: the sidebar becomes a bottom navigation bar with upload in the
 * centre, because that is the action people take most and the thumb reaches it
 * without stretching.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { Link, Outlet, useParams } from "@tanstack/react-router";
import { useT } from "../lib/i18n.js";
import { UnlockBar } from "../features/auth/UnlockBar.js";
import { scheduleWarmFaceModels } from "../features/faces/support.js";
import { useQuery } from "@tanstack/react-query";
import { formatBytes } from "@fc/core";
import { trpc } from "../lib/trpc.js";
import { useAuth } from "../lib/auth.js";
import { Avatar } from "../components/ui/Avatar.js";
import { StorageMeter } from "../components/ui/StorageMeter.js";
import { VerifyEmailGate } from "../features/auth/VerifyEmailGate.js";
import { GroupSwitcher } from "../features/group/GroupSwitcher.js";
import {
  getLastGroupId,
  setLastGroupId,
  subscribeLastGroup,
} from "../lib/lastGroup.js";
import "./AppShell.css";
import { useGroupKey } from "../lib/useGroupKey.js";
import { useKeyHandover } from "../lib/useKeyHandover.js";
import { EmojiIcon } from "../components/ui/EmojiIcon.js";
import { flagFor, primaryCountry } from "../lib/countries.js";

/**
 * @param children What to render in the content area.
 *
 * Optional, and almost always omitted: the shell is a layout route, so its
 * content normally comes from the router. The exception is the index route,
 * which is public — it shows the landing page to a stranger and the app to
 * somebody signed in, and it cannot be a child of the authenticated route
 * without the router redirecting the stranger away before the landing page
 * ever renders.
 */
export function AppShell({ children }: { children?: ReactNode }) {
  const auth = useAuth();
  const t = useT();

  /*
   * Fetch the face models into the browser cache, once, in the background.
   *
   * The *files*, not the inference sessions. Parsing ten megabytes into a
   * session holds it in memory for the life of the tab, which is a poor trade
   * on a phone for something nobody has opened yet. Downloading is the slow
   * half, so doing it here means People is ready when it is wanted instead of
   * starting a ten-megabyte wait at the moment somebody asks for it.
   *
   * Declines by itself on a metered or very slow connection, and on a browser
   * that could not run the model at all.
   */
  useEffect(() => {
    scheduleWarmFaceModels();
  }, []);

  const params = useParams({ strict: false }) as { groupId?: string };
  const routeGroupId = params.groupId;

  /*
   * The bottom bar follows the group you last opened, not the current URL.
   *
   * Half of the app's routes have no group in the path — Home, Admin, an
   * invite. Deriving the bar from the URL alone makes it lose its items the
   * moment you leave a gallery, which reads as the app forgetting where you
   * were. The sidebar still follows the route, because on desktop it sits
   * beside the content and showing another group's albums there would be
   * actively wrong.
   */
  const rememberedGroupId = useSyncExternalStore(
    subscribeLastGroup,
    getLastGroupId,
    () => null,
  );

  useEffect(() => {
    if (routeGroupId) setLastGroupId(routeGroupId);
  }, [routeGroupId]);

  const groupId = routeGroupId;
  const navGroupId = routeGroupId ?? rememberedGroupId ?? undefined;

  /*
   * Opened once, here, for whichever group is on screen.
   *
   * Everything that reads or writes media needs the key open, so it happens at
   * the top rather than in each of them — and the handover below can only be
   * done by somebody who has it, which makes this the natural place for both.
   */
  const keyState = useGroupKey(groupId);
  useKeyHandover(groupId ?? "", keyState === "ready");

  const groups = useQuery(trpc.groups.list.queryOptions());
  const group = useQuery({
    ...trpc.groups.get.queryOptions({ groupId: groupId! }),
    enabled: Boolean(groupId),
  });
  const folders = useQuery({
    ...trpc.groups.folders.queryOptions({ groupId: groupId! }),
    enabled: Boolean(groupId),
  });

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("fc-sidebar") === "collapsed",
  );

  useEffect(() => {
    localStorage.setItem("fc-sidebar", collapsed ? "collapsed" : "expanded");
  }, [collapsed]);

  const trips = (folders.data ?? [])
    .filter((folder) => folder.kind === "trip")
    .slice(0, 8);

  return (
    <div className="fc-shell" data-collapsed={collapsed || undefined}>
      <a className="fc-skip-link" href="#main">
        Skip to content
      </a>

      <aside className="fc-sidebar" aria-label={t("Main navigation")}>
        <div className="fc-sidebar__top">
          <GroupSwitcher
            groups={groups.data ?? []}
            currentGroupId={groupId}
            collapsed={collapsed}
          />
        </div>

        <nav className="fc-sidebar__nav">
          <SidebarSection label={t("Library")} collapsed={collapsed}>
            {/*
              Home sits outside the group check; everything after it stays
              inside one. The whole nav used to be skipped when the path had
              no group, so Admin rendered a sidebar holding nothing but the
              switcher — no way back to the group list short of the browser
              back button.
            */}
            <SidebarLink
              to="/"
              icon={<HomeIcon />}
              label={t("Home")}
              collapsed={collapsed}
            />

            {groupId && (
              <>
                <SidebarLink
                  to="/g/$groupId"
                  params={{ groupId }}
                  search={{}}
                  icon={<GridIcon />}
                  label={t("All photos")}
                  collapsed={collapsed}
                />
                <SidebarLink
                  to="/g/$groupId"
                  params={{ groupId }}
                  search={{ kind: "video" as const }}
                  icon={<VideoIcon />}
                  label={t("Videos")}
                  collapsed={collapsed}
                />
                {/*
                    Albums as a place, not only as a list of individual trips.

                    The sidebar named every album under "Trips" but offered no way
                    to reach the albums screen itself — which is where an album is
                    renamed, given dates, or deleted. On a phone it sits in the
                    tab bar; on a desktop, the widest screen of the two, there was
                    no route to it at all.
                  */}
                <SidebarLink
                  to="/g/$groupId"
                  params={{ groupId }}
                  search={{ view: "albums" as const }}
                  icon={<AlbumIcon />}
                  label={t("Albums")}
                  collapsed={collapsed}
                />
                <SidebarLink
                  to="/g/$groupId"
                  params={{ groupId }}
                  search={{ favorites: true }}
                  icon={<HeartIcon />}
                  label={t("Favourites")}
                  collapsed={collapsed}
                />
                {/*
                    Every screen could send a photograph to the trash and no
                    screen could show it, so "move to trash" read as a polite word
                    for delete and anything sent there by mistake was, as far as
                    anybody could tell, gone.
                  */}
                <SidebarLink
                  to="/g/$groupId"
                  params={{ groupId }}
                  search={{ view: "trash" as const }}
                  icon={<TrashBinIcon />}
                  label={t("Trash")}
                  collapsed={collapsed}
                />
              </>
            )}
          </SidebarSection>

          {groupId && (
            <>
              <SidebarSection label={t("Group")} collapsed={collapsed}>
                <SidebarLink
                  to="/g/$groupId/wall"
                  params={{ groupId }}
                  icon={<WallIcon />}
                  label={t("Wall")}
                  collapsed={collapsed}
                />
              </SidebarSection>

              {folders.isError && !collapsed && (
                <SidebarSection label={t("Trips")} collapsed={collapsed}>
                  <p className="fc-sidebar__error">
                    {t("Couldn’t load albums.")}{" "}
                    <button
                      type="button"
                      onClick={() => void folders.refetch()}
                    >
                      {t("Try again")}
                    </button>
                  </p>
                </SidebarSection>
              )}

              {trips.length > 0 && (
                <SidebarSection label={t("Trips")} collapsed={collapsed}>
                  {trips.map((trip) => (
                    <SidebarLink
                      key={trip.id}
                      to="/g/$groupId"
                      params={{ groupId }}
                      search={{ folder: trip.id }}
                      /*
                       * The album's own colour, where the album is actually
                       * looked for.
                       *
                       * Picking one used to change a single thing: the tint of
                       * the title on the albums grid, which is a screen most
                       * people pass through rather than sit on. In the sidebar
                       * — the list you scan to find a trip — every entry was
                       * the same grey folder. A colour that appears nowhere you
                       * look is a setting that does nothing, so it appears
                       * here, as the dot behind the glyph.
                       */
                      icon={
                        <span
                          className="fc-sidebar__trip-icon"
                          data-label={trip.accentColor ?? undefined}
                          aria-hidden="true"
                        >
                          {trip.emoji ? (
                            <EmojiIcon emoji={trip.emoji} />
                          ) : primaryCountry(trip.countryCodes) ? (
                            <EmojiIcon
                              emoji={flagFor(
                                primaryCountry(trip.countryCodes)!,
                              )}
                            />
                          ) : (
                            "📁"
                          )}
                        </span>
                      }
                      label={trip.name}
                      badge={trip.photoCount + trip.videoCount}
                      collapsed={collapsed}
                    />
                  ))}
                </SidebarSection>
              )}
            </>
          )}
        </nav>

        <div className="fc-sidebar__bottom">
          {group.data && !collapsed && (
            <StorageMeter
              limitBytes={group.data.storage.limitBytes}
              byClass={group.data.storage.byClass}
              className="fc-sidebar__storage"
            />
          )}

          {groupId && (
            <SidebarLink
              to="/g/$groupId/settings"
              params={{ groupId }}
              icon={<SettingsIcon />}
              label={t("Settings")}
              collapsed={collapsed}
            />
          )}

          {auth.user?.isPlatformAdmin && (
            <SidebarLink
              to="/admin"
              icon={<ShieldIcon />}
              label={t("Admin")}
              collapsed={collapsed}
            />
          )}

          <button
            type="button"
            className="fc-sidebar__collapse"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d={collapsed ? "M9 5l7 7-7 7" : "M15 5l-7 7 7 7"} />
            </svg>
          </button>

          {/*
            Your own name is where people look for your own settings, so it is
            the link rather than a seventh item in a list of group things.
            Sign out stays a button beside it: nesting one inside the other
            would make signing out a click away from opening your account.
          */}
          <div className="fc-sidebar__user">
            <Link
              className="fc-sidebar__user-link"
              to="/account"
              title={collapsed ? "Your account" : undefined}
            >
              <Avatar
                name={auth.user?.displayName ?? "?"}
                src={auth.user?.avatarImage ?? undefined}
                size="sm"
              />
              {!collapsed && (
                <span className="fc-sidebar__user-name">
                  {auth.user?.displayName}
                </span>
              )}
            </Link>
            {!collapsed && (
              <div className="fc-sidebar__user-detail">
                <button
                  type="button"
                  className="fc-sidebar__signout"
                  onClick={() => void auth.signOut()}
                >
                  {t("Sign out")}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="fc-main" id="main">
        {/* Above the content, because it explains why the content looks wrong. */}
        <UnlockBar />

        {/* Stands in front of the application, not beside it: the server
            refuses everything group-scoped until the address is confirmed. */}
        <VerifyEmailGate>{children ?? <Outlet />}</VerifyEmailGate>
      </main>

      <nav className="fc-mobile-nav" aria-label={t("Main navigation")}>
        <MobileLink to="/" icon={<HomeIcon />} label={t("Home")} />
        {navGroupId && (
          <MobileLink
            to="/g/$groupId"
            params={{ groupId: navGroupId }}
            search={{}}
            icon={<GridIcon />}
            label={t("Photos")}
          />
        )}
        {/* Albums sit in the bar itself. Buried behind a filter dropdown they
            were effectively invisible, while groups were one tap away. */}
        {navGroupId && (
          <MobileLink
            to="/g/$groupId"
            params={{ groupId: navGroupId }}
            search={{ view: "albums" as const }}
            icon={<AlbumIcon />}
            label={t("Albums")}
          />
        )}
        {navGroupId && (
          <MobileLink
            to="/g/$groupId/wall"
            params={{ groupId: navGroupId }}
            icon={<WallIcon />}
            label={t("Wall")}
          />
        )}
        {navGroupId && (
          <MobileLink
            to="/g/$groupId"
            params={{ groupId: navGroupId }}
            search={{ favorites: true }}
            icon={<HeartIcon />}
            label={t("Favourites")}
          />
        )}
        {navGroupId && (
          <MobileLink
            to="/g/$groupId/settings"
            params={{ groupId: navGroupId }}
            icon={<SettingsIcon />}
            label={t("Settings")}
          />
        )}
      </nav>

      {/*
        Joined, but nobody has handed over the key yet.
       
        This used to be indistinguishable from an unencrypted group, so the
        gallery tried to render sealed files as plain ones and filled with red
        boxes saying "Locked" and nothing else. There is nothing the person
        here can do about it — only a member who already holds the key can
        produce a copy — so the only useful thing to show is what is actually
        happening and who can end it.
      */}
      {keyState === "failed" && (
        <div className="fc-shell__banner" role="status">
          {t("This device cannot open this group with your current key. Ask a trusted group member to restore access from Your account.")} {" "}
          <Link to="/account">{t("Restore group photos")}</Link>
        </div>
      )}

      {keyState === "awaiting-grant" && (
        <div className="fc-shell__banner" role="status">
          {t(
            "You are in this group, but nobody has given this account the key yet. Ask somebody already in it to open the group once — that hands the key over automatically.",
          )}
        </div>
      )}

      {group.data && group.data.group.status === "locked" && (
        <div className="fc-shell__banner" role="status">
          This group is locked and read-only. Contact the administrator.
          {group.data.storage.usedBytes > 0 && (
            <span className="fc-tabular">
              {" "}
              · {formatBytes(group.data.storage.usedBytes)} stored
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SidebarSection({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fc-sidebar__section">
      {!collapsed && <h2 className="fc-sidebar__section-label">{label}</h2>}
      {children}
    </div>
  );
}

interface SidebarLinkProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  collapsed: boolean;
}

function SidebarLink({
  to,
  params,
  search,
  icon,
  label,
  badge,
  collapsed,
}: SidebarLinkProps) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- route strings are validated by the router's own types at the call site
      to={to as any}
      params={params as never}
      search={search as never}
      className="fc-sidebar__link"
      activeProps={{ "data-active": true } as never}
      activeOptions={{ exact: true, includeSearch: true }}
      title={collapsed ? label : undefined}
    >
      <span className="fc-sidebar__icon">{icon}</span>
      {!collapsed && <span className="fc-sidebar__label">{label}</span>}
      {!collapsed && badge !== undefined && badge > 0 && (
        <span className="fc-sidebar__badge fc-tabular">{badge}</span>
      )}
    </Link>
  );
}

function MobileLink({
  to,
  params,
  search,
  icon,
  label,
}: Omit<SidebarLinkProps, "collapsed" | "badge">) {
  return (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see SidebarLink
      to={to as any}
      params={params as never}
      search={search as never}
      className="fc-mobile-nav__link"
      activeProps={{ "data-active": true } as never}
      activeOptions={{ exact: true, includeSearch: true }}
    >
      <span className="fc-mobile-nav__icon">{icon}</span>
      <span className="fc-mobile-nav__label">{label}</span>
    </Link>
  );
}

/* Icons: 24px grid, 1.5 stroke, no fill — one visual family throughout. */
const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const GridIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const VideoIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10.5l5-3v9l-5-3z" />
  </svg>
);

const HeartIcon = () => (
  <svg {...iconProps}>
    <path d="M12 20s-7-4.3-7-9.2A4.1 4.1 0 0 1 12 7.4a4.1 4.1 0 0 1 7 3.4C19 15.7 12 20 12 20z" />
  </svg>
);

/*
 * A gear that fits its box.
 *
 * The previous path was drawn for a larger canvas — it reached past x=23 and
 * x=1 with lobes hanging over both edges — so inside a 24-unit viewBox the
 * teeth were clipped flat on every side and the whole thing read as a smudge
 * rather than a gear.
 */
const SettingsIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3.2" />
    <circle cx="12" cy="12" r="7.3" />
    <path d="M12 3.2v1.5M12 19.3v1.5M20.8 12h-1.5M4.7 12H3.2M18.2 5.8l-1.1 1.1M6.9 17.1l-1.1 1.1M18.2 18.2l-1.1-1.1M6.9 6.9 5.8 5.8" />
  </svg>
);

const ShieldIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l7 3v6c0 4.4-3 7.9-7 9-4-1.1-7-4.6-7-9V6l7-3z" />
  </svg>
);

const HomeIcon = () => (
  <svg {...iconProps}>
    <path d="M3 10.5L12 4l9 6.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

const AlbumIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="6" width="18" height="14" rx="2" />
    <path d="M7 6V4.5A1.5 1.5 0 0 1 8.5 3h7A1.5 1.5 0 0 1 17 4.5V6" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const TrashBinIcon = () => (
  <svg {...iconProps}>
    <path d="M4.5 7h15M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7" />
    <path d="M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
  </svg>
);

const WallIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 13h8M8 16.5h5" />
  </svg>
);
